/**
 * CommandPalette.js — ⌘K quick navigation.
 *
 * Keyboard-first jump to any page the current user can see. The sidebar
 * stays the visible, learnable map; this is the shortcut for people who
 * already know where they're going — the Linear/Notion pattern, scoped
 * deliberately to navigation only (no destructive actions behind a fuzzy
 * matcher).
 *
 * Entries come pre-filtered by RBAC: the caller passes the same nav model
 * the sidebar renders from, so the palette can never offer a page the
 * sidebar wouldn't. Sub-pages (report tabs, security pages) are first-class
 * entries — "compl" hits Compliance Report directly.
 *
 * With an empty query the palette shows recent destinations (localStorage,
 * last 5) so the common case — bouncing between two pages — is two
 * keystrokes: ⌘K, Enter.
 */

import React, { useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Search, CornerDownLeft } from 'lucide-react';

const RECENTS_KEY = 'cmdk_recents';

const readRecents = () => {
  try { return JSON.parse(localStorage.getItem(RECENTS_KEY) || '[]'); }
  catch (_) { return []; }
};

const pushRecent = (path) => {
  try {
    const next = [path, ...readRecents().filter(p => p !== path)].slice(0, 5);
    localStorage.setItem(RECENTS_KEY, JSON.stringify(next));
  } catch (_) {}
};

/**
 * Match scoring, cheapest thing that ranks sensibly:
 * exact prefix > word-boundary prefix > substring > in-order subsequence.
 * Returns null for no match.
 */
function scoreEntry(label, query) {
  const l = label.toLowerCase();
  const q = query.toLowerCase().trim();
  if (!q) return 0;
  if (l.startsWith(q)) return 100;
  const wordHit = l.split(/[\s›/·-]+/).some(w => w.startsWith(q));
  if (wordHit) return 80;
  const idx = l.indexOf(q);
  if (idx >= 0) return 60 - Math.min(idx, 30);
  // subsequence
  let i = 0;
  for (const ch of l) { if (ch === q[i]) i++; if (i === q.length) break; }
  return i === q.length ? 20 : null;
}

export default function CommandPalette({ open, onClose, entries, onNavigate }) {
  const [query, setQuery] = useState('');
  const [activeIdx, setActiveIdx] = useState(0);
  const inputRef = useRef(null);
  const listRef = useRef(null);

  // Reset on every open so the palette always starts fresh.
  useEffect(() => {
    if (open) {
      setQuery('');
      setActiveIdx(0);
      // Focus after the portal paints.
      requestAnimationFrame(() => inputRef.current?.focus());
    }
  }, [open]);

  const results = useMemo(() => {
    if (!query.trim()) {
      const recents = readRecents();
      const recentEntries = recents
        .map(p => entries.find(e => e.path === p))
        .filter(Boolean);
      const rest = entries.filter(e => !recents.includes(e.path));
      return [
        ...recentEntries.map(e => ({ ...e, section: 'Recent' })),
        ...rest.map(e => ({ ...e, section: 'Pages' })),
      ];
    }
    return entries
      .map(e => ({ ...e, score: scoreEntry(e.label, query), section: 'Results' }))
      .filter(e => e.score !== null)
      .sort((a, b) => b.score - a.score);
  }, [entries, query]);

  // Keep the active row in view while arrowing.
  useEffect(() => {
    const el = listRef.current?.querySelector('[data-active="true"]');
    el?.scrollIntoView({ block: 'nearest' });
  }, [activeIdx]);

  if (!open) return null;

  const go = (entry) => {
    if (!entry) return;
    pushRecent(entry.path);
    onNavigate(entry.path);
    onClose();
  };

  const onKeyDown = (e) => {
    if (e.key === 'ArrowDown') { e.preventDefault(); setActiveIdx(i => Math.min(i + 1, results.length - 1)); }
    else if (e.key === 'ArrowUp') { e.preventDefault(); setActiveIdx(i => Math.max(i - 1, 0)); }
    else if (e.key === 'Enter') { e.preventDefault(); go(results[activeIdx]); }
    else if (e.key === 'Escape') { e.preventDefault(); onClose(); }
  };

  let lastSection = null;

  return createPortal(
    <div className="cmdk-overlay" onMouseDown={e => e.target === e.currentTarget && onClose()}>
      <div className="cmdk-panel" role="dialog" aria-label="Quick navigation">
        <div className="cmdk-input-row">
          <Search size={15} style={{ color: 'var(--text-muted)', flexShrink: 0 }} />
          <input
            ref={inputRef}
            className="cmdk-input"
            placeholder="Jump to a page…"
            value={query}
            onChange={e => { setQuery(e.target.value); setActiveIdx(0); }}
            onKeyDown={onKeyDown}
            aria-label="Search pages"
          />
          <kbd className="cmdk-esc">esc</kbd>
        </div>
        <div className="cmdk-list" ref={listRef}>
          {results.length === 0 && (
            <div className="cmdk-empty">No page matches “{query}”.</div>
          )}
          {results.map((r, i) => {
            const Icon = r.icon;
            const showSection = r.section !== lastSection;
            lastSection = r.section;
            return (
              <React.Fragment key={`${r.path}-${i}`}>
                {showSection && <div className="cmdk-section">{r.section}</div>}
                <div
                  className={`cmdk-item${i === activeIdx ? ' active' : ''}`}
                  data-active={i === activeIdx || undefined}
                  onMouseEnter={() => setActiveIdx(i)}
                  onMouseDown={e => { e.preventDefault(); go(r); }}
                  role="option"
                  aria-selected={i === activeIdx}
                >
                  {Icon && <Icon size={15} className="cmdk-item-icon" />}
                  <span className="cmdk-item-label">{r.label}</span>
                  {r.group && <span className="cmdk-item-group">{r.group}</span>}
                  {i === activeIdx && <CornerDownLeft size={13} className="cmdk-enter" />}
                </div>
              </React.Fragment>
            );
          })}
        </div>
      </div>
    </div>,
    document.body
  );
}
